import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { SubmitPublicLeadRequest } from "@greatway/api-client";
import { submitPublicLead, ApiError } from "../../lib/api";
import TurnstileWidget from "../../components/TurnstileWidget";
import { PageHead } from "../../components/site/PageHead";
import { SiteIcon } from "../../components/site/SiteIcon";
import { PRODUCTS } from "../../content/products";
import { BUSINESS, IMAGES } from "../../content/site";

/**
 * Get a quote — /enquire (Story #25445, prototype § #spec-enquire).
 *
 * A restyle of the live F1.5-2 form plus product pre-selection. Fields, validation (BR-WEB-060),
 * the honeypot + Turnstile token (BR-WEB-063) and the POST /api/v1/public/leads request shape are
 * unchanged. The product field lists the 11 website products from static site content
 * (BR-WEB-061) — the sign-in-only catalogue endpoint is no longer called.
 */

type Errors = Record<string, string>;

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address1: string;
  town: string;
  postcode: string;
  productInterest: string;
  comments: string;
  widthM: string;
  lengthM: string;
  heightM: string;
  websiteUrl: string; // honeypot — must stay empty
}

const EMPTY: FormState = {
  firstName: "", lastName: "", phone: "", email: "", address1: "", town: "",
  postcode: "", productInterest: "", comments: "", widthM: "", lengthM: "", heightM: "", websiteUrl: "",
};

/** BR-WEB-060 — unchanged from the live form. */
export function validate(f: FormState): Errors {
  const e: Errors = {};
  if (!f.firstName.trim()) e.firstName = "First name is required.";
  if (!f.lastName.trim()) e.lastName = "Last name is required.";
  if (f.phone.replace(/\D/g, "").length < 8) e.phone = "Enter a valid phone number.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Enter a valid email address.";
  if (!f.address1.trim()) e.address1 = "Street address is required.";
  if (!f.town.trim()) e.town = "Suburb / town is required.";
  if (!/^\d{4}$/.test(f.postcode)) e.postcode = "Enter a 4-digit postcode.";
  return e;
}

/** BR-WEB-062 — optional decimal metres; blank or non-numeric is absent. */
const toDecimal = (s: string): number | undefined => {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

/** BR-WEB-061 — ?product={slug} pre-selects by slug; an unknown slug is ignored. */
function productNameForSlug(slug: string | null): string {
  if (!slug) return "";
  return PRODUCTS.find((p) => p.slug === slug)?.name ?? "";
}

interface FieldProps {
  id: keyof FormState;
  label: ReactNode;
  error?: string;
  children: (a11y: { id: string; "aria-invalid"?: true; "aria-describedby"?: string }) => ReactNode;
}

function Field({ id, label, error, children }: FieldProps) {
  const msgId = `${id}-msg`;
  return (
    <div className={`gw-f${error ? " err" : ""}`} data-f={id}>
      <label htmlFor={id}>{label}</label>
      {children({ id, ...(error ? { "aria-invalid": true as const, "aria-describedby": msgId } : {}) })}
      {error ? <div className="msg" id={msgId}>{error}</div> : null}
    </div>
  );
}

const Optional = ({ children = "(optional)" }: { children?: ReactNode }) => <span className="opt">{children}</span>;

export default function EnquireFormPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState<FormState>(() => ({ ...EMPTY, productInterest: productNameForSlug(params.get("product")) }));
  const [errors, setErrors] = useState<Errors>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  const update = <K extends keyof FormState>(key: K, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(({ [key]: _cleared, ...rest }) => rest);
  };

  const showErrors = (e: Errors) => {
    setErrors(e);
    // Move focus to the summary so keyboard and screen-reader users land on what to fix.
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (submitting) return;
    setSendFailed(false);
    const e = validate(form);
    if (Object.keys(e).length > 0) {
      showErrors(e);
      return;
    }
    setErrors({});

    const body: SubmitPublicLeadRequest = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address1: form.address1.trim(),
      town: form.town.trim(),
      postcode: form.postcode.trim(),
      productInterest: form.productInterest || null,
      comments: form.comments.trim() || null,
      widthM: toDecimal(form.widthM) ?? null,
      lengthM: toDecimal(form.lengthM) ?? null,
      heightM: toDecimal(form.heightM) ?? null,
      cfTurnstileResponse: turnstileToken || null,
      websiteUrl: form.websiteUrl || null, // honeypot
    };

    setSubmitting(true);
    try {
      const result = await submitPublicLead(body);
      // Blind: the token is populated only on a genuine create. Either way we go to the confirmation page.
      // BR-WEB-064 — replace, so browser back cannot return to (and re-submit) the filled form.
      const token = result.confirmationToken;
      navigate(token ? `/enquire/confirmation#token=${encodeURIComponent(token)}` : "/enquire/confirmation", {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.problem?.errors) {
        // Field keys arrive PascalCase (errors.FirstName) — normalise the first letter (gotcha #19890).
        const fieldErrors: Errors = {};
        for (const [k, msgs] of Object.entries(err.problem.errors)) {
          const key = k.charAt(0).toLowerCase() + k.slice(1);
          fieldErrors[key] = msgs[0] ?? "Invalid value.";
        }
        showErrors(fieldErrors);
      } else {
        // 429 / 503 / network: keep every value and offer the phone number.
        setSendFailed(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const errorList = Object.values(errors);

  return (
    <>
      <PageHead
        crumbs={[{ label: "Get a quote" }]}
        title="Get a free measure & quote."
        lead="Tell us about your project. Fields marked * are required — it takes about two minutes, and there’s no account to create."
        bg={IMAGES.homeBanner2}
      />
      <div className="gw-wrap gw-formwrap">
        <form className="gw-form" onSubmit={onSubmit} noValidate aria-label="Enquiry">
          <div
            ref={summaryRef}
            tabIndex={-1}
            role="alert"
            data-testid="error-summary"
            className={`gw-errsum${errorList.length ? " show" : ""}`}
          >
            {errorList.length ? (
              <>
                <b>
                  Please check {errorList.length} field{errorList.length > 1 ? "s" : ""}:
                </b>{" "}
                {errorList.join(" ")}
              </>
            ) : null}
          </div>

          <fieldset className="gw-fs">
            <legend><i>1</i>Your details</legend>
            <div className="gw-row">
              <Field id="firstName" label="First name *" error={errors.firstName}>
                {(a) => <input {...a} type="text" autoComplete="given-name" value={form.firstName} onChange={(e) => update("firstName", e.target.value)} />}
              </Field>
              <Field id="lastName" label="Last name *" error={errors.lastName}>
                {(a) => <input {...a} type="text" autoComplete="family-name" value={form.lastName} onChange={(e) => update("lastName", e.target.value)} />}
              </Field>
            </div>
            <div className="gw-row">
              <Field id="phone" label="Phone *" error={errors.phone}>
                {(a) => <input {...a} type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} />}
              </Field>
              <Field id="email" label="Email *" error={errors.email}>
                {(a) => <input {...a} type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => update("email", e.target.value)} />}
              </Field>
            </div>
          </fieldset>

          <fieldset className="gw-fs">
            <legend><i>2</i>Where’s the job?</legend>
            <Field id="address1" label="Street address *" error={errors.address1}>
              {(a) => <input {...a} type="text" autoComplete="address-line1" value={form.address1} onChange={(e) => update("address1", e.target.value)} />}
            </Field>
            <div className="gw-row3">
              <Field id="town" label="Suburb / town *" error={errors.town}>
                {(a) => <input {...a} type="text" autoComplete="address-level2" value={form.town} onChange={(e) => update("town", e.target.value)} />}
              </Field>
              <Field id="postcode" label="Postcode *" error={errors.postcode}>
                {(a) => <input {...a} type="text" inputMode="numeric" autoComplete="postal-code" maxLength={4} value={form.postcode} onChange={(e) => update("postcode", e.target.value)} />}
              </Field>
            </div>
          </fieldset>

          <fieldset className="gw-fs">
            <legend><i>3</i>Your project</legend>
            <Field id="productInterest" label="What are you interested in?" error={errors.productInterest}>
              {(a) => (
                <select {...a} value={form.productInterest} onChange={(e) => update("productInterest", e.target.value)}>
                  <option value="">Select a product…</option>
                  {PRODUCTS.map((p) => (
                    <option key={p.slug} value={p.name}>{p.name}</option>
                  ))}
                </select>
              )}
            </Field>
            <div className={`gw-f${errors.widthM || errors.lengthM || errors.heightM ? " err" : ""}`}>
              <div role="group" aria-labelledby="dims-label">
                <label id="dims-label">
                  Approx. dimensions <Optional>(optional, metres)</Optional>
                </label>
                <div className="gw-dims">
                  {(["widthM", "lengthM", "heightM"] as const).map((k) => {
                    const name = { widthM: "Width", lengthM: "Length", heightM: "Height" }[k];
                    return (
                      <div key={k}>
                        <input
                          id={k}
                          aria-label={`${name} (m)`}
                          type="text"
                          inputMode="decimal"
                          placeholder={name}
                          value={form[k]}
                          onChange={(e) => update(k, e.target.value)}
                        />
                        <span aria-hidden="true">m</span>
                      </div>
                    );
                  })}
                </div>
                {errors.widthM || errors.lengthM || errors.heightM ? (
                  <div className="msg">{errors.widthM || errors.lengthM || errors.heightM}</div>
                ) : null}
              </div>
            </div>
            <Field id="comments" label={<>Anything else we should know? <Optional /></>} error={errors.comments}>
              {(a) => (
                <textarea
                  {...a}
                  rows={4}
                  maxLength={2000}
                  placeholder="e.g. a gable patio off the back deck, about 6 × 4 m, Colorbond Monument"
                  value={form.comments}
                  onChange={(e) => update("comments", e.target.value)}
                />
              )}
            </Field>
            {/* Honeypot — hidden from real users; bots that fill it are rejected by the API (BR-WEB-063). */}
            <div className="gw-hp" aria-hidden="true">
              <label htmlFor="websiteUrl">Leave this field empty</label>
              <input id="websiteUrl" type="text" tabIndex={-1} autoComplete="off" value={form.websiteUrl} onChange={(e) => update("websiteUrl", e.target.value)} />
            </div>
          </fieldset>

          <TurnstileWidget onToken={setTurnstileToken} />

          {sendFailed ? (
            <div className="gw-errsum show" role="alert" data-testid="send-error">
              We couldn’t send your enquiry. Please try again or call <a href={BUSINESS.tel}>{BUSINESS.phone}</a>.
            </div>
          ) : null}

          <button className="gw-btn gw-btn-red gw-btn-lg" type="submit" disabled={submitting} style={{ width: "100%" }}>
            {submitting ? "Sending…" : (
              <>
                Send my enquiry <SiteIcon name="arrow" size={18} />
              </>
            )}
          </button>
          <p className="gw-fine">
            We handle your details in line with our privacy commitments. Enquiries are used only to prepare your quote.
          </p>
        </form>

        <aside className="gw-aside" aria-label="About your enquiry">
          <div className="gw-box">
            <h2 className="mb-[10px] text-[22px]">What happens next</h2>
            <ul className="gw-next">
              <li>
                <b>1</b>
                <p><strong>We review it</strong>A local rep reviews every enquiry and gets back to you within two business days.</p>
              </li>
              <li>
                <b>2</b>
                <p><strong>Free measure &amp; quote</strong>We arrange a time to measure up on site and talk through options.</p>
              </li>
              <li>
                <b>3</b>
                <p><strong>Add photos (optional)</strong>After you submit you can add photos of your space — they help us quote more accurately.</p>
              </li>
            </ul>
          </div>
          <div className="gw-box">
            <h2 className="mb-[10px] text-[22px]">Prefer to talk?</h2>
            <p>Call us during office hours, or visit the display centre.</p>
            <a className="gw-btn gw-btn-line" href={BUSINESS.tel}>
              <SiteIcon name="phone" size={18} /> {BUSINESS.phone}
            </a>
          </div>
        </aside>
      </div>
    </>
  );
}
